// src/pages/staff/EditStaffPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import StaffForm from '../components/StaffForm';

// Hooks
import { useStaff } from '../hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { FormPage, FormSection, FormSwitchField, FormActions } from '../../../components/common/form';

export default function EditStaffPage() {
  const { t } = useTranslation(['staff', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { staffId } = useParams();
  const { showSuccess, showError } = useSnackbar();

  // State
  const [staffMember, setStaffMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    hire_date: null,
    is_active: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    staff,
    fetchDepartmentStaff,
    updateStaff,
    loading: updating,
    error,
    clearError,
  } = useStaff();

  // Load staff data
  useEffect(() => {
    const loadStaffData = async () => {
      if (!staffId) return;

      try {
        setLoading(true);
        await fetchDepartmentStaff({ include_inactive: true });
      } catch (error) {
        showError(t('staff:errors.load_staff_data_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadStaffData();
  }, [staffId, fetchDepartmentStaff, showError, t]);

  // Find and set current staff member
  useEffect(() => {
    if (staff && staffId) {
      const foundStaff = staff.find(s => s.staff_member_id === staffId);
      if (foundStaff) {
        setStaffMember(foundStaff);
        setFormData({
          name: foundStaff.name || '',
          hire_date: foundStaff.hire_date || null,
          is_active: foundStaff.is_active !== undefined ? foundStaff.is_active : true,
        });
      } else {
        setStaffMember(null);
      }
    }
  }, [staff, staffId]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleActiveToggle = useCallback((event) => {
    handleInputChange('is_active', event.target.checked);
  }, [handleInputChange]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('staff:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('staff:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('staff:validation.name_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm() || !staffMember) {
      return;
    }

    try {
      const result = await updateStaff(staffMember.staff_member_id, formData);

      if (result.success) {
        showSuccess(t('staff:success.staff_updated'));
        navigate(`/staff/${staffMember.staff_member_id}`);
      } else {
        showError(result.error || t('staff:errors.update_failed'));
      }
    } catch (error) {
      showError(t('staff:errors.unexpected_error'));
    }
  }, [validateForm, staffMember, formData, updateStaff, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack(staffMember ? `/staff/${staffMember.staff_member_id}` : '/staff');
  }, [navigateBack, staffMember]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!staffMember) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigateBack('/staff')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('staff:messages.staff_not_found')}
          </Typography>
        </Box>
        <Alert severity="error">
          {t('staff:messages.staff_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('staff:edit_staff_member')}
      subtitle={t('staff:form.edit_title', { name: staffMember.name })}
      crumb={staffMember.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <StaffForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={updating}
        mode="edit"
      />

      <FormSection title={t('staff:account_status_form.title')}>
        <FormSwitchField
          checked={formData.is_active}
          onChange={handleActiveToggle}
          disabled={updating}
          label={formData.is_active ? t('staff:account_status_form.active_account') : t('staff:account_status_form.inactive_account')}
          description={formData.is_active ? t('staff:account_status_form.active_description') : t('staff:account_status_form.inactive_description')}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={updating ? t('staff:actions.updating') : t('staff:actions.update_staff_member')}
        submitIcon={<SaveIcon />}
        loading={updating}
      />
    </FormPage>
  );
}
